import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import LinkButton from '@/components/atoms/linkButton';

import IconShoppingCart from '~/svg/IconShoppingCart.svg';
import IconRocket from '~/svg/IconRocket.svg';

import { textGlobal } from './PricingTable';

export type PricingRowProps = {
  title: string;
  discount?: string;
  discountLabel?: string;
  price: number;
  pricePer: 'IP' | 'GB';
  priceTotal?: number;
  priceTotalOld?: number;
  priceTotalPostfixText?: string;
  buttonText: string;
  buttonVariant?: 'filled' | 'outlined';
  url: string;
  outlineColor?: 'grey' | 'black';
  outlineText?: string;
  icon: React.ReactNode;
  showMoneyBack?: boolean;
  moneyBack?: string;
};

export default function PricingRow({
  title,
  discount,
  discountLabel,
  price,
  pricePer,
  priceTotal,
  priceTotalPostfixText,
  buttonText,
  buttonVariant,
  url,
  outlineColor,
  outlineText,
  showMoneyBack,
  moneyBack,
}: PricingRowProps) {
  const planId = url?.split('planId=')[1] || '';
  const trackId = `${title}-${planId}`;

  const formattedPricePer = pricePer.replaceAll('-', ' ').replaceAll('ww', '');
  const outlineBorderClasses = {
    grey: '',
    black: 'border-neutral-N99',
  } as const;

  const transformNumberToWithTwoDecimals = (number: number) => {
    const numberString = number.toString();
    if (numberString === '0') {
      return '0.00';
    }
    return number.toFixed(2);
  };

  const transformPriceTotal = (priceTotal: number) => {
    const formatter = new Intl.NumberFormat('en-US');
    return formatter.format(priceTotal);
  };

  return (
    <div
      data-tracker-component='card'
      className={cn(
        'shadow-card bg-neutral-N00 border-neutral-N02 m:flex m:py-4 relative mb-2 w-full items-center justify-between rounded-[0.625rem] border border-solid px-6 py-6',
        outlineColor && outlineBorderClasses[outlineColor]
      )}
    >
      <div className='m:pb-0 m:justify-start flex basis-1/4 items-center justify-center pb-6'>
        <div className='m:w-fit m:text-left bg-neutral-N04 m:bg-transparent flex h-7 w-full items-center justify-center rounded-2xl text-center'>
          <p className='text-body-m m:text-body-l '>{title}</p>
        </div>
        {outlineText && (
          <Badge
            colors='neutral-N99'
            variant='filled'
            classNames={{
              root: 'font-bold m:ml-2 max-h-4 absolute m:static -top-2',
            }}
          >
            {outlineText}
          </Badge>
        )}
      </div>

      <div className='m:w-fit m:justify-normal m:pb-0 flex w-full basis-1/4 items-center justify-between pb-4'>
        <p>
          <span className='text-subtitle-l'>
            ${transformNumberToWithTwoDecimals(price)}
          </span>
          <span className='text-neutral-N50 text-body-s ml-1'>
            /{formattedPricePer}
          </span>
        </p>

        <div className={cn(!discount && 'invisible')}>
          <Badge
            colors='lightGradient'
            variant='filled'
            classNames={{ root: 'ml-3 font-bold max-h-4' }}
          >
            {discount && (
              <>
                {discountLabel} {discount}
              </>
            )}
          </Badge>
        </div>
      </div>

      {priceTotal && (
        <div className=' m:pb-0 grid	basis-1/4 justify-items-start pb-6'>
          <span className='m:text-body-l text-body-m'>
            ${transformPriceTotal(priceTotal)} total
          </span>
          <div className='text-neutral-N40 text-body-s m:min-h-min m:flex-row m:text-left min-h-10 flex-col lg:flex'>
            <span className='mr-1'>{priceTotalPostfixText}</span>
            {showMoneyBack && (
              <div className='flex'>
                <p className='m:flex mr-1 hidden'>/ </p>
                <p> {moneyBack}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className='basis-1/4 text-right'>
        <LinkButton
          data-tracker-context={trackId}
          href={url}
          variant={buttonVariant || 'filled'}
          color='primary'
          slim={false}
          fullwidth
          openInNewTab={false}
          leftSection={
            textGlobal.buyNow === buttonText ? (
              <IconShoppingCart />
            ) : (
              <IconRocket />
            )
          }
          classNames={{
            root: 'group w-full m:max-w-[160px] max-h-8',
            inner: 'flex items-center justify-center gap-4',
            rightSection:
              'group-hover:translate-x-2 duration-300 ease-in-out transition',
          }}
        >
          {buttonText}
        </LinkButton>
      </div>
    </div>
  );
}
